<template>
  <div class="time-picker-container">
    <!-- Desktop: PrimeVue DatePicker with timeOnly -->
    <template v-if="!isTouchDevice">
      <DatePicker
        v-model="desktopTime"
        timeOnly
        hourFormat="24"
        :placeholder="placeholder"
        :disabled="disabled"
        :invalid="invalid"
        :inputId="inputId"
        fluid
        class="w-full"
        :pt="{ panel: { style: 'direction: ltr' }, pcInput: {root: { style: 'text-align: right;' }} }"
      />
    </template>

    <!-- Touch/Mobile: scroll wheel picker -->
    <template v-else>
      <div class="time-picker-input-wrapper" v-if="!hideInput">
        <input
          :id="inputId"
          type="text"
          :value="formattedValue"
          :placeholder="placeholder"
          :disabled="disabled"
          :class="['time-picker-input', { 'p-invalid': invalid }]"
          @click="handleInputClick"
          readonly
          style="direction: rtl"
          aria-label="Time picker"
          :aria-invalid="invalid"
          :aria-describedby="invalid ? `${inputId}-error` : null"
        />
        <i class="pi pi-clock input-icon"></i>
      </div>

      <Dialog
        v-model:visible="isVisible"
        modal
        dismissableMask
        :showHeader="false"
        :style="{ width: '90vw', maxWidth: '380px', margin: '0 auto' }"
        :contentStyle="{ padding: '0', borderRadius: '16px', overflow: 'hidden' }"
        transition="slide-up"
      >

        <div class="pt-4 text-center">
          <h3 class="m-0 text-lg font-semibold text-gray-800">בחירת שעה</h3>
        </div>

        <div class="time-picker-body">
          <div class="picker-column" ref="minutesContainer">
            <div
              class="picker-scroll"
              @touchstart="handleTouchStart($event, 'minutes')"
              @touchmove="handleTouchMove($event, 'minutes')"
              @touchend="handleTouchEnd($event, 'minutes')"
              @mousedown="handleMouseDown($event, 'minutes')"
              @mousemove="handleMouseMove($event, 'minutes')"
              @mouseup="handleMouseUp($event, 'minutes')"
              @mouseleave="handleMouseUp($event, 'minutes')"
              :style="{ transform: `translateY(${minutesTranslateY}px)` }"
            >
              <div class="spacer"></div>
              <div
                v-for="minute in minutes"
                :key="minute"
                :class="['picker-item', {
                    active: selectedMinute === minute,
                    disabled: !isMinuteValid(minute)
                  }]"
                :data-value="minute"
              >
                {{ minute.toString().padStart(2, '0') }}
              </div>
              <div class="spacer"></div>
            </div>
          </div>

          <div class="picker-separator">:</div>

          <div class="picker-column" ref="hoursContainer">
            <div
              class="picker-scroll"
              @touchstart="handleTouchStart($event, 'hours')"
              @touchmove="handleTouchMove($event, 'hours')"
              @touchend="handleTouchEnd($event, 'hours')"
              @mousedown="handleMouseDown($event, 'hours')"
              @mousemove="handleMouseMove($event, 'hours')"
              @mouseup="handleMouseUp($event, 'hours')"
              @mouseleave="handleMouseUp($event, 'hours')"
              :style="{ transform: `translateY(${hoursTranslateY}px)` }"
            >
              <div class="spacer"></div>
              <div
                v-for="hour in hours"
                :key="hour"
                :class="['picker-item', {
                    active: selectedHour === hour,
                    disabled: !isHourValid(hour)
                  }]"
                :data-value="hour"
              >
                {{ hour.toString().padStart(2, '0') }}
              </div>
              <div class="spacer"></div>
            </div>
          </div>

          <div class="picker-column period-column" v-if="!is24Hour">
            <div
              class="picker-scroll"
              @touchstart="handleTouchStart($event, 'period')"
              @touchmove="handleTouchMove($event, 'period')"
              @touchend="handleTouchEnd($event, 'period')"
              @mousedown="handleMouseDown($event, 'period')"
              @mousemove="handleMouseMove($event, 'period')"
              @mouseup="handleMouseUp($event, 'period')"
              @mouseleave="handleMouseUp($event, 'period')"
              :style="{ transform: `translateY(${periodTranslateY}px)` }"
            >
              <div class="spacer"></div>
              <div
                v-for="period in periods"
                :key="period"
                :class="['picker-item', { active: selectedPeriod === period }]"
                :data-value="period"
              >
                {{ period }}
              </div>
              <div class="spacer"></div>
            </div>
          </div>
          <div class="selection-indicator"></div>
        </div>

        <div class="px-6 pb-4">
          <drawer-footer
            @approve-click="handleSave"
            @cancel-click="handleCancel"
          />
        </div>
      </Dialog>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import Dialog from 'primevue/dialog';
import DatePicker from 'primevue/datepicker';

const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

const props = defineProps({
  modelValue: {
    type: Date,
    default: () => new Date()
  },
  visible: {
    type: Boolean,
    default: false
  },
  is24Hour: {
    type: Boolean,
    default: false
  },
  startTime: {
    type: Date,
    default: null
  },
  endTime: {
    type: Date,
    default: null
  },
  placeholder: {
    type: String,
    default: 'בחרו זמן'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  invalid: {
    type: Boolean,
    default: false
  },
  hideInput: {
    type: Boolean,
    default: false
  },
  inputId: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['update:modelValue', 'update:visible']);

// Desktop time picker bridge
const desktopTime = computed({
  get: () => props.modelValue ?? new Date(),
  set: (val) => {
    if (val) emit('update:modelValue', val);
  }
});

// Constants
const ITEM_HEIGHT = 48;
const VISIBLE_ITEMS = 5;
const FRICTION = 0.95;
const MIN_VELOCITY = 0.5;

// State
const selectedHour = ref(12);
const selectedMinute = ref(0);
const selectedPeriod = ref('AM');
const isVisible = ref(props.visible);

const hoursTranslateY = ref(0);
const minutesTranslateY = ref(0);
const periodTranslateY = ref(0);

// Touch/Mouse tracking
const isDragging = ref(false);
const currentColumn = ref(null);
const startY = ref(0);
const currentY = ref(0);
const velocityY = ref(0);
const lastY = ref(0);
const lastTime = ref(0);

// Refs
const hoursContainer = ref(null);
const minutesContainer = ref(null);

// Animation frame reference
let animationId = null;

// Computed
const hours = computed(() => {
  if (props.is24Hour) {
    return Array.from({ length: 24 }, (_, i) => i);
  }
  return Array.from({ length: 12 }, (_, i) => i === 0 ? 12 : i);
});

const minutes = computed(() => {
  return Array.from({ length: 60 }, (_, i) => i);
});

const periods = ['AM', 'PM'];

const formattedValue = computed(() => {
  if (!props.modelValue) return '';

  const date = props.modelValue;
  const hours = date.getHours();
  const mins = date.getMinutes();

  if (props.is24Hour) {
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  } else {
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const period = hours >= 12 ? 'PM' : 'AM';
    return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
  }
});

// Methods
const handleInputClick = () => {
  if (!props.disabled) {
    emit('update:visible', true);
    isVisible.value = true;
  }
};

const isTimeValid = (hour, minute) => {
  if (!props.startTime && !props.endTime) return true;

  const testDate = new Date();
  let testHour = hour;

  if (!props.is24Hour) {
    if (selectedPeriod.value === 'PM' && hour !== 12) {
      testHour += 12;
    } else if (selectedPeriod.value === 'AM' && hour === 12) {
      testHour = 0;
    }
  }

  testDate.setHours(testHour, minute, 0, 0);

  if (props.startTime) {
    const startDate = new Date();
    startDate.setHours(props.startTime.getHours(), props.startTime.getMinutes(), 0, 0);
    if (testDate < startDate) return false;
  }

  if (props.endTime) {
    const endDate = new Date();
    endDate.setHours(props.endTime.getHours(), props.endTime.getMinutes(), 0, 0);
    if (testDate > endDate) return false;
  }

  return true;
};

const isHourValid = (hour) => {
  // Check if any minute in this hour would be valid
  for (let m = 0; m < 60; m++) {
    if (isTimeValid(hour, m)) return true;
  }
  return false;
};

const isMinuteValid = (minute) => {
  return isTimeValid(selectedHour.value, minute);
};

const getClosestValidValue = (value, items, type) => {
  // First check if current value is valid
  if (type === 'hours' && isHourValid(value)) return value;
  if (type === 'minutes' && isMinuteValid(value)) return value;

  // Find closest valid value
  let closestDist = Infinity;
  let closestValue = value;

  items.forEach(item => {
    const isValid = type === 'hours' ? isHourValid(item) : isMinuteValid(item);
    if (isValid) {
      const dist = Math.abs(item - value);
      if (dist < closestDist) {
        closestDist = dist;
        closestValue = item;
      }
    }
  });

  return closestValue;
};

const getClosestValue = (translateY, items) => {
  const index = Math.round(-translateY / ITEM_HEIGHT);
  return items[Math.max(0, Math.min(index, items.length - 1))];
};

const snapToValue = (column, value, animated = true) => {
  let targetY = 0;
  let items = [];

  if (column === 'hours') {
    items = hours.value;
    targetY = -items.indexOf(value) * ITEM_HEIGHT;
    if (animated) {
      animateToPosition('hours', targetY);
    } else {
      hoursTranslateY.value = targetY;
    }
  } else if (column === 'minutes') {
    items = minutes.value;
    targetY = -items.indexOf(value) * ITEM_HEIGHT;
    if (animated) {
      animateToPosition('minutes', targetY);
    } else {
      minutesTranslateY.value = targetY;
    }
  } else if (column === 'period') {
    targetY = -periods.indexOf(value) * ITEM_HEIGHT;
    if (animated) {
      animateToPosition('period', targetY);
    } else {
      periodTranslateY.value = targetY;
    }
  }
};

const animateToPosition = (column, targetY) => {
  const startY = column === 'hours' ? hoursTranslateY.value :
    column === 'minutes' ? minutesTranslateY.value :
      periodTranslateY.value;
  const distance = targetY - startY;
  const duration = 300;
  const startTime = Date.now();

  const animate = () => {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easeProgress = 1 - Math.pow(1 - progress, 3); // Ease out cubic

    const currentY = startY + distance * easeProgress;

    if (column === 'hours') {
      hoursTranslateY.value = currentY;
    } else if (column === 'minutes') {
      minutesTranslateY.value = currentY;
    } else if (column === 'period') {
      periodTranslateY.value = currentY;
    }

    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  };

  animate();
};

const handleTouchStart = (e, column) => {
  e.preventDefault();
  isDragging.value = true;
  currentColumn.value = column;
  startY.value = e.touches[0].clientY;
  lastY.value = e.touches[0].clientY;
  lastTime.value = Date.now();
  velocityY.value = 0;

  if (animationId) {
    cancelAnimationFrame(animationId);
  }
};

const handleMouseDown = (e, column) => {
  e.preventDefault();
  isDragging.value = true;
  currentColumn.value = column;
  startY.value = e.clientY;
  lastY.value = e.clientY;
  lastTime.value = Date.now();
  velocityY.value = 0;

  if (animationId) {
    cancelAnimationFrame(animationId);
  }
};

const handleTouchMove = (e, column) => {
  if (!isDragging.value || currentColumn.value !== column) return;
  e.preventDefault();

  const clientY = e.touches[0].clientY;
  const deltaY = clientY - startY.value;
  const now = Date.now();
  const deltaTime = now - lastTime.value;

  if (deltaTime > 0) {
    velocityY.value = (clientY - lastY.value) / deltaTime * 16;
  }

  lastY.value = clientY;
  lastTime.value = now;

  if (column === 'hours') {
    hoursTranslateY.value += deltaY;
    startY.value = clientY;
  } else if (column === 'minutes') {
    minutesTranslateY.value += deltaY;
    startY.value = clientY;
  } else if (column === 'period') {
    periodTranslateY.value += deltaY;
    startY.value = clientY;
  }
};

const handleMouseMove = (e, column) => {
  if (!isDragging.value || currentColumn.value !== column) return;
  e.preventDefault();

  const clientY = e.clientY;
  const deltaY = clientY - startY.value;
  const now = Date.now();
  const deltaTime = now - lastTime.value;

  if (deltaTime > 0) {
    velocityY.value = (clientY - lastY.value) / deltaTime * 16;
  }

  lastY.value = clientY;
  lastTime.value = now;

  if (column === 'hours') {
    hoursTranslateY.value += deltaY;
    startY.value = clientY;
  } else if (column === 'minutes') {
    minutesTranslateY.value += deltaY;
    startY.value = clientY;
  } else if (column === 'period') {
    periodTranslateY.value += deltaY;
    startY.value = clientY;
  }
};

const handleTouchEnd = (e, column) => {
  if (!isDragging.value || currentColumn.value !== column) return;

  isDragging.value = false;
  applyMomentum(column);
};

const handleMouseUp = (e, column) => {
  if (!isDragging.value || currentColumn.value !== column) return;

  isDragging.value = false;
  applyMomentum(column);
};

const applyMomentum = (column) => {
  let velocity = velocityY.value;
  let translateY = column === 'hours' ? hoursTranslateY.value :
    column === 'minutes' ? minutesTranslateY.value :
      periodTranslateY.value;

  const animate = () => {
    velocity *= FRICTION;
    translateY += velocity;

    // Update the appropriate column
    if (column === 'hours') {
      hoursTranslateY.value = translateY;
    } else if (column === 'minutes') {
      minutesTranslateY.value = translateY;
    } else if (column === 'period') {
      periodTranslateY.value = translateY;
    }

    // Continue animation if velocity is significant
    if (Math.abs(velocity) > MIN_VELOCITY) {
      animationId = requestAnimationFrame(animate);
    } else {
      // Snap to nearest value
      let items = column === 'hours' ? hours.value :
        column === 'minutes' ? minutes.value :
          periods;
      const value = getClosestValue(translateY, items);
      const validValue = column === 'period' ? value :
        getClosestValidValue(value, items, column);

      if (column === 'hours') {
        selectedHour.value = validValue;
        // Re-validate minutes when hour changes
        const validMinute = getClosestValidValue(selectedMinute.value, minutes.value, 'minutes');
        if (validMinute !== selectedMinute.value) {
          selectedMinute.value = validMinute;
          snapToValue('minutes', validMinute, true);
        }
      } else if (column === 'minutes') {
        selectedMinute.value = validValue;
      } else if (column === 'period') {
        selectedPeriod.value = validValue;
        // Re-validate hour and minutes when period changes
        const validHour = getClosestValidValue(selectedHour.value, hours.value, 'hours');
        if (validHour !== selectedHour.value) {
          selectedHour.value = validHour;
          snapToValue('hours', validHour, true);
        }
        const validMinute = getClosestValidValue(selectedMinute.value, minutes.value, 'minutes');
        if (validMinute !== selectedMinute.value) {
          selectedMinute.value = validMinute;
          snapToValue('minutes', validMinute, true);
        }
      }

      snapToValue(column, validValue, true);
    }
  };

  animate();
};

const handleCancel = () => {
  emit('update:visible', false);
  isVisible.value = false;
};

const handleSave = () => {
  const date = new Date();
  let hour = selectedHour.value;

  if (!props.is24Hour) {
    if (selectedPeriod.value === 'PM' && hour !== 12) {
      hour += 12;
    } else if (selectedPeriod.value === 'AM' && hour === 12) {
      hour = 0;
    }
  }

  date.setHours(hour);
  date.setMinutes(selectedMinute.value);
  date.setSeconds(0);

  emit('update:modelValue', date);
  emit('update:visible', false);
  isVisible.value = false;
};

// Initialize values from modelValue
const initializeFromModelValue = () => {
  const date = props.modelValue || new Date();
  let hour = date.getHours();

  selectedMinute.value = date.getMinutes();

  if (!props.is24Hour) {
    selectedPeriod.value = hour >= 12 ? 'PM' : 'AM';
    if (hour > 12) {
      hour -= 12;
    } else if (hour === 0) {
      hour = 12;
    }
  }

  selectedHour.value = hour;

  // Validate and adjust to closest valid time if needed
  const validHour = getClosestValidValue(selectedHour.value, hours.value, 'hours');
  if (validHour !== selectedHour.value) {
    selectedHour.value = validHour;
  }

  const validMinute = getClosestValidValue(selectedMinute.value, minutes.value, 'minutes');
  if (validMinute !== selectedMinute.value) {
    selectedMinute.value = validMinute;
  }

  // Set initial positions
  snapToValue('hours', selectedHour.value, false);
  snapToValue('minutes', selectedMinute.value, false);
  if (!props.is24Hour) {
    snapToValue('period', selectedPeriod.value, false);
  }
};

// Lifecycle
onMounted(() => {
  initializeFromModelValue();
});

onUnmounted(() => {
  if (animationId) {
    cancelAnimationFrame(animationId);
  }
});

// Watch for hour changes to revalidate minutes
watch(() => selectedHour.value, () => {
  if (isVisible.value) {
    const validMinute = getClosestValidValue(selectedMinute.value, minutes.value, 'minutes');
    if (validMinute !== selectedMinute.value) {
      selectedMinute.value = validMinute;
      snapToValue('minutes', validMinute, true);
    }
  }
});

// Watch for period changes to revalidate times
watch(() => selectedPeriod.value, () => {
  if (isVisible.value && !props.is24Hour) {
    const validHour = getClosestValidValue(selectedHour.value, hours.value, 'hours');
    if (validHour !== selectedHour.value) {
      selectedHour.value = validHour;
      snapToValue('hours', validHour, true);
    }

    const validMinute = getClosestValidValue(selectedMinute.value, minutes.value, 'minutes');
    if (validMinute !== selectedMinute.value) {
      selectedMinute.value = validMinute;
      snapToValue('minutes', validMinute, true);
    }
  }
});

// Watch for visibility changes
watch(() => isVisible, (newVal) => {
  if (newVal) {
    initializeFromModelValue();
  }
});
</script>

<style scoped lang="scss">
@use '../../../../assets/css/vars' as *;

/* ANIMATION STYLES (Added) */
.slide-up-enter-active {
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-up-leave-active {
  transition: transform 0.3s ease-in;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100vh);
}

.time-picker-container {
  direction: ltr;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Input Field Styles - PrimeVue Compatible */
.time-picker-input-wrapper {
  position: relative;
  width: 100%;
}

.time-picker-input {
  width: 100%;
  padding: 0.75rem 2.5rem 0.75rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background-color: #ffffff;
  color: #495057;
  transition: background-color 0.2s, color 0.2s, border-color 0.2s, box-shadow 0.2s;
  cursor: pointer;
  font-family: inherit;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

.time-picker-input:hover:not(:disabled) {
  border-color: #ced4da;
}

.time-picker-input:focus {
  outline: 0 none;
  outline-offset: 0;
  box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.5);
  border-color: #3b82f6;
}

.time-picker-input:disabled {
  background-color: #e9ecef;
  color: #6c757d;
  opacity: 1;
  cursor: not-allowed;
}

.time-picker-input.p-invalid {
  border-color: #f87171;
}

.time-picker-input.p-invalid:focus {
  box-shadow: 0 0 0 0.2rem rgba(248, 113, 113, 0.2);
}

.input-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  pointer-events: none;
  font-size: 1rem;
}

/* Time Picker Body Styles */
.time-picker-body {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 240px;
  position: relative;
  overflow: hidden;
  margin-bottom: 20px;
}

.picker-column {
  width: 80px;
  height: 240px;
  overflow: hidden;
  position: relative;
  touch-action: none;
  user-select: none;
}

.period-column {
  width: 60px;
}

.picker-scroll {
  position: absolute;
  width: 100%;
  transition: none;
  cursor: grab;
}

.picker-scroll:active {
  cursor: grabbing;
}

.picker-item {
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #999;
  transition: all 0.3s ease;
}

.picker-item.active {
  color: #000;
  font-weight: 600;
  font-size: 28px;
}

.picker-item.disabled {
  color: #e0e0e0;
  opacity: 0.5;
  text-decoration: line-through;
}

.spacer {
  height: 96px;
}

.picker-separator {
  font-size: 28px;
  font-weight: 600;
  margin: 0 10px;
  color: #000;
}

.selection-indicator {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: calc(100%);
  height: 48px;
  border-top: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  pointer-events: none;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.02);
}
</style>
